import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Button from '@material-ui/core/Button';
import ChallengeView from '../challenge/ChallengeView';
import SolutionView from '../challenge/SolutionView';
import TextAnswerInput from '../answer/TextAnswerInput';
import { answerInputProps } from '../displayVariant';
import { answerHasCase } from '../explanation';
import { PRACTICE_ERRORS } from '../errors';
import { placeholderBlocks } from '../challenge/blocks';

const VERDICT_MESSAGES = Object.freeze({
    CORRECT: 'conventionPage.practice.verdict.correct',
    CASE_ONLY: 'conventionPage.practice.verdict.caseOnly',
    WRONG: 'conventionPage.practice.verdict.wrong',
});

const ERROR_MESSAGES = Object.freeze({
    [PRACTICE_ERRORS.CHALLENGE_EXPIRED]: 'conventionPage.practice.expired',
    [PRACTICE_ERRORS.TOO_MANY]: 'conventionPage.practice.tooMany',
    [PRACTICE_ERRORS.UNAVAILABLE]: 'conventionPage.practice.unavailable',
});

// How a verdict of the engine is displayed. The page never compares the answer itself.
export const verdictDisplay = (verdict) => {
    if (verdict === 'CORRECT') return { tone: 'ok', messageId: VERDICT_MESSAGES.CORRECT, showSolution: false };
    if (verdict === 'CASE_ONLY') return { tone: 'warn', messageId: VERDICT_MESSAGES.CASE_ONLY, showSolution: true };
    if (verdict === 'WRONG') return { tone: 'err', messageId: VERDICT_MESSAGES.WRONG, showSolution: true };
    return null;
};

export const ChallengePlaceholder = ({ blockSizes }) => (
    <div className="hia-ack-challenge hia-ack-challenge--placeholder" aria-hidden="true">
        {placeholderBlocks(blockSizes).map((size, index) => (
            <span key={index} className={size > 16 ? 'hia-ack-block hia-ack-block--long' : 'hia-ack-block'}>
                {Array.from({ length: size }).map((_, cell) => <span key={cell} className="hia-ack-cell">·</span>)}
            </span>
        ))}
    </div>
);

const PracticeSection = ({ exercise, score, parameters, disabled, acknowledged, onCheck, onAnother, expiredNotice }) => {
    const [answer, setAnswer] = useState('');
    const itemId = exercise.item && exercise.item.practiceId;
    useEffect(() => {
        setAnswer('');
    }, [itemId]);

    if (exercise.status === 'hidden' || exercise.status === 'idle') return null;

    const blockSizes = parameters && parameters.blocks;
    const locked = disabled || acknowledged || exercise.limited;
    const display = verdictDisplay(exercise.verdict);
    const submit = (event) => {
        event.preventDefault();
        if (!locked && answer.length > 0) onCheck(answer);
    };

    return (
        <section className="hia-ack-card" aria-labelledby="hia-ack-h-practice" data-section="practice">
            <h2 id="hia-ack-h-practice" className="hia-ack-h2"><FormattedMessage id="conventionPage.practice.heading" /></h2>
            {expiredNotice}
            <p className="hia-ack-note"><FormattedMessage id="conventionPage.practice.intro" /></p>

            {exercise.status === 'loading' && !exercise.item && (
                <>
                    <ChallengePlaceholder blockSizes={blockSizes} />
                    <p className="hia-ack-note"><FormattedMessage id="conventionPage.practice.loading" /></p>
                </>
            )}
            {exercise.item && <ChallengeView item={exercise.item} blockSizes={blockSizes} />}

            {exercise.item && (
                <form className="hia-ack-form" onSubmit={submit} noValidate>
                    <label htmlFor="hia-ack-answer" className="hia-ack-label">
                        <FormattedMessage id={answerHasCase(parameters) ? 'conventionPage.practice.inputLabel' : 'conventionPage.practice.inputLabelPlain'} />
                    </label>
                    <div className="hia-ack-form-row">
                        <TextAnswerInput
                            id="hia-ack-answer"
                            value={answer}
                            onChange={setAnswer}
                            disabled={locked}
                            {...answerInputProps(parameters)}
                        />
                        <Button type="submit" variant="contained" color="primary" disabled={locked || exercise.checking || answer.length === 0}>
                            <FormattedMessage id={exercise.checking ? 'conventionPage.practice.checking' : 'conventionPage.practice.check'} />
                        </Button>
                        <Button
                            type="button"
                            variant="outlined"
                            color="primary"
                            onClick={onAnother}
                            disabled={locked || exercise.status === 'loading'}
                        >
                            <FormattedMessage id="conventionPage.practice.another" />
                        </Button>
                    </div>
                </form>
            )}

            <div className="hia-ack-live" aria-live="polite" data-testid="verdict-region">
                {display && !exercise.checking && (
                    <div className={`hia-ack-result hia-ack-result--${display.tone}`} data-verdict={exercise.verdict}>
                        <p className="hia-ack-result-title"><FormattedMessage id={display.messageId} /></p>
                        {display.showSolution && exercise.typed !== null && (
                            <p>
                                <FormattedMessage id="conventionPage.practice.typed" />{' '}
                                <span className="hia-ack-mono" data-testid="typed-answer">{exercise.typed}</span>
                            </p>
                        )}
                        {display.showSolution && exercise.solution && (
                            <SolutionView
                                item={exercise.item}
                                solution={exercise.solution}
                                blockSizes={blockSizes}
                                answerLabelId="conventionPage.practice.expected"
                            />
                        )}
                    </div>
                )}
                {exercise.errorKind && ERROR_MESSAGES[exercise.errorKind] && (
                    <p className="hia-ack-inline-error"><FormattedMessage id={ERROR_MESSAGES[exercise.errorKind]} /></p>
                )}
                {acknowledged && <p className="hia-ack-note"><FormattedMessage id="conventionPage.practice.afterValidation" /></p>}
            </div>

            {score.total > 0 && (
                <p className="hia-ack-score" data-testid="score">
                    <FormattedMessage id="conventionPage.practice.score" values={{ correct: score.correct, total: score.total }} />
                </p>
            )}
        </section>
    );
};

export default PracticeSection;
